import { useState, useEffect } from 'react';
import { getFarms, createFarm, updateFarmBoundary } from '../services/api';
import { calculateAreaHectares } from '../utils/polygonCalculations';

export const useFarms = (setMessage, setMode, setIsEditingFarm) => {
    const [farms, setFarms] = useState([]);
    const [selectedFarmId, setSelectedFarmId] = useState(() => {
        const saved = localStorage.getItem('selectedFarmId');
        return saved ? (isNaN(saved) ? saved : parseInt(saved)) : 'new';
    });
    const [farmId, setFarmId] = useState(null);
    const [farmName, setFarmName] = useState('');
    const [farmBoundary, setFarmBoundary] = useState(null);
    const [farmArea, setFarmArea] = useState(0);

    const loadFarms = async () => {
        try {
            const fetchedFarms = await getFarms();
            setFarms(fetchedFarms);
            
            if (selectedFarmId !== 'new') {
                const stillExists = fetchedFarms.find(f => f.id === selectedFarmId);
                if (stillExists) {
                    handleFarmSelect(selectedFarmId, fetchedFarms);
                    return;
                }
            }

            if (fetchedFarms.length > 0) {
                handleFarmSelect(fetchedFarms[0].id, fetchedFarms);
            } else {
                setMode('view');
                setSelectedFarmId('new');
                localStorage.setItem('selectedFarmId', 'new');
            }
        } catch (err) {
            console.error("Could not load farms", err);
            setMessage({ text: 'Failed to load farms', type: 'error', open: true });
        }
    };

    const handleFarmSelect = (id, farmList = farms) => {
        setSelectedFarmId(id);
        localStorage.setItem('selectedFarmId', id);
        
        if (id === 'new') {
            setFarmId(null);
            setFarmName('');
            setFarmBoundary(null);
            setMode('view');
            setIsEditingFarm(false);
            setFarmArea(0);
        } else {
            const farm = farmList.find(f => f.id === id);
            if (farm) {
                setFarmId(farm.id);
                setFarmName(farm.fastighetsbeteckning);
                setFarmBoundary({
                    type: 'Feature',
                    geometry: farm.geojson,
                    properties: {}
                });
                
                if (farm.geojson) {
                    const areaHectares = calculateAreaHectares({ type: 'Feature', geometry: farm.geojson });
                    setFarmArea(areaHectares);
                } else {
                    setFarmArea(0);
                }
                setMode('view');
                setIsEditingFarm(false);
            }
        }
    };

    const handleSaveFarm = async (feature, drawData) => {
        if (!farmName) {
            setMessage({ text: 'Please enter a farm name', type: 'warning', open: true });
            return;
        }

        try {
            let id = farmId;
            if (!id) {
                const result = await createFarm({ fastighetsbeteckning: farmName });
                id = result.id;
                setFarmId(id);
            }

            const featureToSave = feature || (drawData?.features?.length > 0 ? drawData.features[0] : null);

            if (featureToSave) {
                await updateFarmBoundary(id, featureToSave);
                setFarmBoundary(featureToSave);
                setFarmArea(calculateAreaHectares(featureToSave));
            }

            setMessage({ text: 'Farm boundary saved successfully!', type: 'success', open: true });
            setIsEditingFarm(false);
            setMode('view');
            
            await loadFarms();
            handleFarmSelect(id);
        } catch (err) {
            console.error("Error in handleSaveFarm:", err);
            setMessage({ text: 'Failed to save farm: ' + err.message, type: 'error', open: true });
        }
    };

    return {
        farms,
        selectedFarmId,
        farmId,
        farmName,
        setFarmName,
        farmBoundary,
        farmArea,
        loadFarms,
        handleFarmSelect,
        handleSaveFarm
    };
};
